import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { templateApiService } from '../services/templateApiService';
import { apiService } from '../services/api';
import {
  OfficialApiInbox,
  CreateTemplateData,
  TemplateComponent,
  TemplateButton,
  TemplateCategory,
  HeaderFormat,
  SupportedLanguages,
  WhatsAppTemplate
} from '../types/template';
import { TemplatePreview } from './TemplatePreview';
import { TooltipHelp } from './TooltipHelp';
import toast from 'react-hot-toast';

interface CreateTemplateModalProps {
  inbox: OfficialApiInbox;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateTemplateModal({ inbox, isOpen, onClose, onSuccess }: CreateTemplateModalProps) {
  const { theme } = useTheme();
  const [creating, setCreating] = useState(false);
  const [duplicateNameAlert, setDuplicateNameAlert] = useState<WhatsAppTemplate | null>(null);

  // Form data
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('pt_BR');
  const [category, setCategory] = useState<TemplateCategory>('UTILITY');

  // Components
  const [hasHeader, setHasHeader] = useState(false);
  const [headerType, setHeaderType] = useState<HeaderFormat>('TEXT');
  const [headerText, setHeaderText] = useState('');
  
  // Media Upload States
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [headerMediaName, setHeaderMediaName] = useState('');
  const [headerMediaId, setHeaderMediaId] = useState(''); // Media ID da Meta
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null); // URL local para preview
  const [savedMediaList, setSavedMediaList] = useState<any[]>([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  
  const [bodyText, setBodyText] = useState('');
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [variableType, setVariableType] = useState<'number' | 'name'>('number');
  const [bodyVariableExamples, setBodyVariableExamples] = useState<{ [key: string]: string }>({});
  const [hasVariableAtEdges, setHasVariableAtEdges] = useState(false);
  
  const [hasFooter, setHasFooter] = useState(false);
  const [footerText, setFooterText] = useState('');
  
  const [hasButtons, setHasButtons] = useState(false);
  const [buttons, setButtons] = useState<TemplateButton[]>([]);

  // Função para carregar mídias salvas
  const loadSavedMedia = useCallback(async (mediaType: string) => {
    try {
      setLoadingMedia(true);
      const response = await apiService.get('/official-api-media/list', { 
        params: { mediaType } 
      });
      setSavedMediaList(response.data || []);
      console.log(`📚 ${response.data?.length || 0} mídias ${mediaType} carregadas`);
    } catch (error) {
      console.error('Erro ao carregar mídias:', error);
      setSavedMediaList([]);
    } finally {
      setLoadingMedia(false);
    }
  }, []);

  // Função para reutilizar mídia da biblioteca
  const handleSelectSavedMedia = async (media: any) => {
    try {
      console.log('📂 Reutilizando mídia da biblioteca:', media.name);
      setMediaPreviewUrl(media.thumbnailUrl || media.fileUrl);
      setHeaderMediaName(media.name);
      
      // Buscar o arquivo da URL para fazer novo upload retomável
      const response = await fetch(media.fileUrl);
      const blob = await response.blob();
      const file = new File([blob], media.fileName || media.name, { type: media.mimeType });
      setSelectedFile(file);
      
      toast.success(`✅ Mídia "${media.name}" selecionada! Clique em "Fazer Upload" para enviar.`);
    } catch (error) {
      console.error('Erro ao selecionar mídia:', error);
      toast.error('Erro ao carregar mídia da biblioteca');
    }
  };

  // Carregar mídias salvas quando mudar tipo de header
  useEffect(() => {
    if (hasHeader && headerType !== 'TEXT') {
      loadSavedMedia(headerType);
    } else {
      setSavedMediaList([]);
      setMediaPreviewUrl(null);
      setSelectedFile(null);
    }
  }, [hasHeader, headerType, loadSavedMedia]);

  if (!isOpen) return null;

  const validateName = (value: string) => {
    return /^[a-z0-9_]+$/.test(value);
  };

  // Função para substituir variáveis pelos valores das amostras na pré-visualização
  const getPreviewText = (text: string): string => {
    if (!text) return '';
    
    let previewText = text;
    
    // Substituir variáveis pelos valores das amostras
    Object.keys(bodyVariableExamples).forEach(variable => {
      const value = bodyVariableExamples[variable];
      if (value && value.trim()) {
        // variable já contém as chaves: {{1}}, {{2}}, etc.
        // Escapar caracteres especiais de regex
        const escapedVariable = variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedVariable, 'g');
        previewText = previewText.replace(regex, value);
      }
    });
    
    return previewText;
  };

  const handleNameChange = (value: string) => {
    // Converter para lowercase e remover caracteres inválidos
    const cleaned = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setName(cleaned);
    setDuplicateNameAlert(null);
  };

  const findTemplateWithSameName = async (templateName: string): Promise<WhatsAppTemplate | null> => {
    const existingTemplates = await templateApiService.getTemplates(inbox.providerConfig.phoneNumberId);
    const normalizedName = templateName.toLowerCase();

    return existingTemplates.find((template) => template.name.toLowerCase() === normalizedName) || null;
  };

  // Função para contar quantas variáveis numéricas já existem no texto
  const countExistingVariables = (text: string): number => {
    const matches = text.match(/\{\{\d+\}\}/g);
    if (!matches) return 0;
    
    // Extrair os números e encontrar o maior
    const numbers = matches.map(match => {
      const num = match.match(/\d+/);
      return num ? parseInt(num[0]) : 0;
    });
    
    return Math.max(0, ...numbers);
  };

  // Função para detectar todas as variáveis no bodyText
  const detectBodyVariables = (text: string): string[] => {
    const matches = text.match(/\{\{.+?\}\}/g);
    if (!matches) return [];
    
    // Remover duplicatas e retornar em ordem
    const uniqueVars = Array.from(new Set(matches));
    return uniqueVars.sort();
  };

  // Função para verificar se há variáveis no início ou fim do texto
  const checkVariableAtEdges = (text: string): boolean => {
    if (!text.trim()) return false;
    
    const trimmedText = text.trim();
    // Verifica se começa ou termina com variável {{...}}
    const startsWithVariable = /^\{\{.+?\}\}/.test(trimmedText);
    const endsWithVariable = /\{\{.+?\}\}$/.test(trimmedText);
    
    return startsWithVariable || endsWithVariable;
  };

  // Função para atualizar bodyText e limpar exemplos de variáveis removidas
  const handleBodyTextChange = (newText: string) => {
    setBodyText(newText);
    
    // Verificar se há variáveis no início ou fim
    setHasVariableAtEdges(checkVariableAtEdges(newText));
    
    // Detectar variáveis no novo texto
    const currentVariables = detectBodyVariables(newText);
    
    // Limpar exemplos de variáveis que foram removidas
    setBodyVariableExamples(prevExamples => {
      const newExamples: { [key: string]: string } = {};
      currentVariables.forEach(variable => {
        newExamples[variable] = prevExamples[variable] || '';
      });
      return newExamples;
    });
  };

  // Função para inserir variável no textarea na posição do cursor
  const insertVariableInBody = () => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) return;

    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = bodyText.substring(0, cursorPosition);
    const textAfterCursor = bodyText.substring(cursorPosition);

    let variableToInsert = '';
    let newCursorPosition = cursorPosition;

    if (variableType === 'number') {
      // Para tipo número, inserir automaticamente o próximo número disponível
      const nextVarNumber = countExistingVariables(bodyText) + 1;
      variableToInsert = `{{${nextVarNumber}}}`;
      newCursorPosition = cursorPosition + variableToInsert.length;
    } else {
      // Para tipo nome, inserir {{}} e posicionar cursor entre as chaves
      variableToInsert = '{{}}';
      newCursorPosition = cursorPosition + 2; // Posicionar entre as chaves
    }

    const newText = textBeforeCursor + variableToInsert + textAfterCursor;
    handleBodyTextChange(newText);

    // Focar no textarea e posicionar o cursor
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleMediaUpload = async (file: File) => {
    // Gerar nome automático se não fornecido
    const mediaName = headerMediaName.trim() || `Template_${headerType}_${Date.now()}`;

    // Validar tipo de arquivo
    if (headerType === 'VIDEO') {
      if (file.type !== 'video/mp4') {
        toast.error('❌ Apenas vídeos no formato MP4 são aceitos');
        return;
      }
      if (file.size > 14 * 1024 * 1024) {
        toast.error('❌ Vídeo muito grande. Tamanho máximo: 14 MB');
        return;
      }
    }

    if (headerType === 'IMAGE') {
      if (!file.type.startsWith('image/')) {
        toast.error('❌ Arquivo deve ser uma imagem');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('❌ Imagem muito grande. Tamanho máximo: 5 MB');
        return;
      }
    }

    if (headerType === 'DOCUMENT') {
      if (file.type !== 'application/pdf') {
        toast.error('❌ Apenas documentos PDF são aceitos');
        return;
      }
      if (file.size > 100 * 1024 * 1024) {
        toast.error('❌ Documento muito grande. Tamanho máximo: 100 MB');
        return;
      }
    }

    // Validar se APP_ID está configurado antes de fazer upload
    if (!inbox.providerConfig.appId) {
      toast.error(
        '❌ Configure o APP_ID em "Configurar Perfil Comercial" antes de criar templates com mídia',
        { duration: 6000 }
      );
      return;
    }

    setUploadingMedia(true);

    try {
      console.log('📤 Fazendo upload retomável para Meta API...');
      
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      // Upload usando API Resumable (necessário para templates com mídia)
      const response = await apiService.post(
        `/resumable-media/upload/${inbox.providerConfig.phoneNumberId}`,
        uploadFormData
      );

      console.log('📥 Resposta da Meta (Resumable Upload):', response);
      console.log('   File Handle:', response.fileHandle);
      
      // Guardar File Handle da Meta (não mais Media ID)
      setHeaderMediaId(response.fileHandle); // Reutilizamos o state mas agora é fileHandle
      setHeaderMediaUrl(file.name); // Só para referência visual
      
      // Atualizar nome se foi gerado automaticamente
      if (!headerMediaName.trim()) {
        setHeaderMediaName(mediaName);
      }

      toast.success('✅ Mídia enviada para Meta com sucesso!');
    } catch (error: any) {
      console.error('❌ Erro no upload retomável:', error);
      toast.error(error.message || 'Erro ao fazer upload de mídia para templates');
    } finally {
      setUploadingMedia(false);
    }
  };

  const addButton = () => {
    if (buttons.length >= 10) {
      toast.error('Máximo de 10 botões permitidos');
      return;
    }

    setButtons([...buttons, {
      type: 'QUICK_REPLY',
      text: ''
    }]);
  };

  const removeButton = (index: number) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const updateButton = (index: number, field: keyof TemplateButton, value: any) => {
    const updated = [...buttons];
    updated[index] = { ...updated[index], [field]: value };
    setButtons(updated);
  };

  const buildComponents = (): TemplateComponent[] => {
    const components: TemplateComponent[] = [];

    // Header
    if (hasHeader) {
      if (headerType === 'TEXT') {
        components.push({
          type: 'HEADER',
          format: 'TEXT',
          text: headerText
        });
      } else {
        // Para IMAGE, VIDEO ou DOCUMENT com mídia
        const headerComponent: TemplateComponent = {
          type: 'HEADER',
          format: headerType
        };

        // Se tem File Handle da Meta (Resumable Upload), adicionar como exemplo
        if (headerMediaId) {
          // headerMediaId agora armazena o fileHandle retornado pelo upload retomável
          const fileHandleStr = String(headerMediaId).trim();
          
          headerComponent.example = {
            header_handle: [fileHandleStr] // File handle do upload retomável
          };
          console.log(`✅ Usando File Handle da Meta no template: ${fileHandleStr}`);
        }

        components.push(headerComponent);
      }
    }

    // Body (obrigatório)
    const bodyComponent: TemplateComponent = {
      type: 'BODY',
      text: bodyText
    };

    // Adicionar exemplos de variáveis se houver
    const variables = detectBodyVariables(bodyText);
    if (variables.length > 0) {
      // Criar array de exemplos na ordem das variáveis
      const exampleValues = variables.map(variable => bodyVariableExamples[variable] || '');
      
      // Formato Meta API: example.body_text = [[exemplo1, exemplo2, ...]]
      bodyComponent.example = {
        body_text: [exampleValues]
      };
      
      console.log('📝 Variáveis detectadas:', variables);
      console.log('📝 Exemplos fornecidos:', exampleValues);
    }

    components.push(bodyComponent);

    // Footer - TEMPORARIAMENTE DESABILITADO v1.2.6
    // if (hasFooter && footerText.trim()) {
    //   components.push({
    //     type: 'FOOTER',
    //     text: footerText
    //   });
    // }

    // Buttons
    if (hasButtons && buttons.length > 0) {
      const validButtons = buttons.filter(b => b.text.trim());
      if (validButtons.length > 0) {
        components.push({
          type: 'BUTTONS',
          buttons: validButtons
        });
      }
    }

    return components;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!name.trim()) {
      toast.error('Nome do template é obrigatório');
      return;
    }

    if (!validateName(name)) {
      toast.error('Nome deve conter apenas letras minúsculas, números e underscore');
      return;
    }

    if (!bodyText.trim()) {
      toast.error('Texto do corpo é obrigatório');
      return;
    }

    if (bodyText.length > 1024) {
      toast.error('Texto do corpo muito longo (máximo 1024 caracteres)');
      return;
    }

    // Validar se há variáveis no início ou fim
    if (hasVariableAtEdges) {
      toast.error('As variáveis não podem estar no início ou no fim do modelo.');
      return;
    }

    // Validar exemplos de variáveis no body
    const bodyVariables = detectBodyVariables(bodyText);
    if (bodyVariables.length > 0) {
      const missingExamples = bodyVariables.filter(variable => {
        const example = bodyVariableExamples[variable];
        return !example || !example.trim();
      });

      if (missingExamples.length > 0) {
        toast.error(`Preencha exemplos para todas as variáveis: ${missingExamples.join(', ')}`);
        return;
      }
    }

    if (hasFooter && footerText.length > 60) {
      toast.error('Texto do rodapé muito longo (máximo 60 caracteres)');
      return;
    }

    if (hasHeader && headerType === 'TEXT' && !headerText.trim()) {
      toast.error('Texto do cabeçalho é obrigatório quando tipo TEXT');
      return;
    }

    if (hasHeader && headerType !== 'TEXT' && !headerMediaId) {
      toast.error('Faça upload da mídia para enviar o template para análise da Meta');
      return;
    }

    if (hasButtons) {
      for (const btn of buttons) {
        if (!btn.text.trim()) {
          toast.error('Todos os botões devem ter texto');
          return;
        }
        if (btn.text.length > 20) {
          toast.error('Texto do botão muito longo (máximo 20 caracteres)');
          return;
        }
        if (btn.type === 'URL' && !btn.url) {
          toast.error('Botões de tipo URL devem ter URL');
          return;
        }
      }
    }

    try {
      setCreating(true);

      const templateName = name.trim();
      const existingTemplate = await findTemplateWithSameName(templateName);

      if (existingTemplate) {
        setDuplicateNameAlert(existingTemplate);
        return;
      }

      const templateData: CreateTemplateData = {
        name: templateName,
        language,
        category,
        components: buildComponents()
      };

      console.log('📤 Criando template...');
      console.log('📦 Payload completo:', JSON.stringify(templateData, null, 2));
      console.log('📦 Components:', templateData.components);
      console.log('📦 phoneNumberId:', inbox.providerConfig.phoneNumberId);

      await templateApiService.createTemplate(
        inbox.providerConfig.phoneNumberId,
        templateData
      );

      toast.success(
        'Template criado com sucesso! Aguarde a aprovação da Meta (pode levar alguns minutos).',
        { duration: 5000 }
      );

      onSuccess();
    } catch (error: any) {
      console.error('❌ Erro ao criar template:', error);
      toast.error(error.message || 'Erro ao criar template');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setName('');
    setLanguage('pt_BR');
    setCategory('UTILITY');
    setHasHeader(false);
    setHeaderType('TEXT');
    setHeaderText('');
    setHeaderMediaUrl('');
    setHeaderMediaName('');
    setHeaderMediaId(''); // Limpar Media ID da Meta
    setSelectedFile(null);
    setMediaPreviewUrl(null); // Limpar preview
    setSavedMediaList([]); // Limpar biblioteca
    setBodyText('');
    setBodyVariableExamples({}); // Limpar exemplos de variáveis
    setHasVariableAtEdges(false); // Limpar validação de variáveis nas pontas
    setHasFooter(false);
    setFooterText('');
    setHasButtons(false);
    setButtons([]);
    setDuplicateNameAlert(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div
        className={`rounded-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          theme === 'dark' ? 'bg-[#2A2A2A]' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div className={`p-6 border-b ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-n-slate-12 flex items-center gap-2">
                <span>Criar Novo Template</span>
                <TooltipHelp id="templates_create" />
              </h2>
              <p className="text-sm mt-1 text-n-slate-11">
                {inbox.name} • {inbox.phoneNumber}
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                theme === 'dark'
                  ? 'hover:bg-gray-700 text-gray-400'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Formulário */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Informações Básicas */}
              <div>
                <h3 className={`text-lg font-semibold mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Informações Básicas
                </h3>

                <div className="space-y-4">
                  {/* Nome */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Nome do Template *
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="exemplo_template_nome"
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      required
                    />
                    <p className={`text-xs mt-1 ${
                      theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      Apenas letras minúsculas, números e underscore
                    </p>
                  </div>

                  {/* Idioma */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Idioma *
                    </label>
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      required
                    >
                      {SupportedLanguages.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Categoria */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Categoria *
                    </label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value as TemplateCategory)}
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      required
                    >
                      <option value="MARKETING">📢 Marketing (Promoções, ofertas)</option>
                      <option value="UTILITY">🔧 Utilidade (Confirmações, atualizações)</option>
                      <option value="AUTHENTICATION">🔐 Autenticação (OTP, códigos)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Header */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Cabeçalho (Opcional)
                  </h3>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasHeader}
                      onChange={(e) => setHasHeader(e.target.checked)}
                      className="mr-2"
                    />
                    <span className={`text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Adicionar cabeçalho
                    </span>
                  </label>
                </div>

                {hasHeader && (
                  <div className="space-y-4">
                    <select
                      value={headerType}
                      onChange={(e) => setHeaderType(e.target.value as HeaderFormat)}
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="TEXT">Texto</option>
                      <option value="IMAGE">Imagem</option>
                      <option value="VIDEO">Vídeo</option>
                      <option value="DOCUMENT">Documento</option>
                    </select>

                    {headerType === 'TEXT' && (
                      <input
                        type="text"
                        value={headerText}
                        onChange={(e) => setHeaderText(e.target.value)}
                        placeholder="Texto do cabeçalho"
                        className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    )}

                    {headerType !== 'TEXT' && (
                      <div className="space-y-3">
                        <div className={`p-3 rounded-lg ${
                          theme === 'dark'
                            ? 'bg-blue-900/20 border border-blue-800'
                            : 'bg-blue-50 border border-blue-200'
                        }`}>
                          <p className={`text-sm ${
                            theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                          }`}>
                            ℹ️ <strong>Upload de Mídia para Análise:</strong> Faça upload de {headerType === 'IMAGE' ? 'uma imagem' : headerType === 'VIDEO' ? 'um vídeo' : 'um documento'} de exemplo. A mídia será enviada para a Meta junto com o template para análise.
                          </p>
                        </div>
                        
                        <p className={`text-xs ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          💡 Obrigatório: A Meta requer um exemplo de mídia para analisar templates com cabeçalho de {headerType === 'IMAGE' ? 'imagem' : headerType === 'VIDEO' ? 'vídeo' : 'documento'}
                        </p>

                        {/* Nome da Mídia */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Nome da Mídia (Opcional - será gerado automaticamente se vazio)
                          </label>
                          <input
                            type="text"
                            value={headerMediaName}
                            onChange={(e) => setHeaderMediaName(e.target.value)}
                            placeholder={`Ex: Exemplo ${headerType === 'IMAGE' ? 'Imagem' : headerType === 'VIDEO' ? 'Vídeo' : 'PDF'} Template`}
                            className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>

                        {/* Seleção de Arquivo */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Arquivo *
                          </label>
                          <input
                            type="file"
                            accept={
                              headerType === 'IMAGE' ? 'image/jpeg,image/jpg,image/png' :
                              headerType === 'VIDEO' ? 'video/mp4' :
                              'application/pdf'
                            }
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                console.log('📂 Arquivo selecionado:', file.name);
                                setSelectedFile(file);
                                
                                // Criar preview local da imagem/vídeo
                                if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
                                  const previewUrl = URL.createObjectURL(file);
                                  setMediaPreviewUrl(previewUrl);
                                }
                              }
                            }}
                            disabled={uploadingMedia}
                            className={`w-full px-4 py-2 rounded-lg border text-sm ${
                              theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                          
                          {headerType === 'VIDEO' && (
                            <p className="text-xs text-orange-500 mt-2">
                              ⚠️ Apenas vídeos MP4 com até 14 MB
                            </p>
                          )}
                          {headerType === 'IMAGE' && (
                            <p className="text-xs text-blue-500 mt-2">
                              💡 JPG, JPEG ou PNG com até 5 MB
                            </p>
                          )}
                          {headerType === 'DOCUMENT' && (
                            <p className="text-xs text-blue-500 mt-2">
                              💡 PDF com até 100 MB
                            </p>
                          )}
                        </div>

                        {/* Botão de Upload */}
                        {selectedFile && !headerMediaUrl && (
                          <button
                            type="button"
                            onClick={() => handleMediaUpload(selectedFile)}
                            disabled={uploadingMedia}
                            className={`w-full py-3 rounded-lg font-medium transition-colors ${
                              uploadingMedia
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                          >
                            {uploadingMedia ? (
                              <span className="flex items-center justify-center gap-2">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                Enviando...
                              </span>
                            ) : (
                              <span className="flex items-center justify-center gap-2">
                                📤 {headerType === 'IMAGE' ? 'Fazer Upload da Imagem' : headerType === 'VIDEO' ? 'Fazer Upload do Vídeo' : 'Fazer Upload do PDF'}
                              </span>
                            )}
                          </button>
                        )}

                        {/* Confirmação de Upload */}
                        {headerMediaUrl && (
                          <div className={`p-3 rounded-lg ${
                            theme === 'dark'
                              ? 'bg-green-900/20 border border-green-800'
                              : 'bg-green-50 border border-green-200'
                          }`}>
                            <p className={`text-sm font-medium flex items-center gap-2 ${
                              theme === 'dark' ? 'text-green-400' : 'text-green-700'
                            }`}>
                              <span className="text-xl">✅</span>
                              {headerType === 'IMAGE' ? 'Imagem' : headerType === 'VIDEO' ? 'Vídeo' : 'Documento'} carregado com sucesso!
                            </p>
                            <p className={`text-xs mt-1 ${
                              theme === 'dark' ? 'text-green-300' : 'text-green-600'
                            }`}>
                              {headerMediaName}
                            </p>
                          </div>
                        )}

                        {/* Biblioteca de Mídias Salvas */}
                        {savedMediaList.length > 0 && (
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              📚 Mídias Salvas ({savedMediaList.length})
                            </label>
                            <p className={`text-xs mb-2 ${
                              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                            }`}>
                              Clique em uma mídia para reutilizá-la
                            </p>
                            
                            <div className={`max-h-60 overflow-y-auto rounded-lg border p-2 ${
                              theme === 'dark' 
                                ? 'bg-gray-800 border-gray-700' 
                                : 'bg-gray-50 border-gray-200'
                            }`}>
                              {savedMediaList.map(media => (
                                <div 
                                  key={media.id}
                                  onClick={() => handleSelectSavedMedia(media)}
                                  className={`flex items-center gap-3 p-2 rounded mb-2 cursor-pointer transition-colors ${
                                    selectedFile && headerMediaName === media.name
                                      ? theme === 'dark' 
                                        ? 'bg-blue-900/30 border-2 border-blue-500' 
                                        : 'bg-blue-50 border-2 border-blue-500'
                                      : theme === 'dark' 
                                        ? 'hover:bg-gray-700 border-2 border-transparent' 
                                        : 'hover:bg-gray-100 border-2 border-transparent'
                                  }`}
                                >
                                  {/* Thumbnail */}
                                  {media.thumbnailUrl ? (
                                    <img 
                                      src={media.thumbnailUrl} 
                                      alt={media.name}
                                      className="w-16 h-16 object-cover rounded"
                                    />
                                  ) : (
                                    <div className={`w-16 h-16 rounded flex items-center justify-center ${
                                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                                    }`}>
                                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                    </div>
                                  )}
                                  
                                  {/* Info */}
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium truncate ${
                                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                      {media.name}
                                    </p>
                                    <p className={`text-xs ${
                                      theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                    }`}>
                                      {media.mediaType} • {(media.fileSize / 1024).toFixed(0)} KB
                                    </p>
                                  </div>
                                  
                                  {/* Indicador de seleção */}
                                  {selectedFile && headerMediaName === media.name && (
                                    <div className="text-blue-500">
                                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {loadingMedia && (
                          <div className="text-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                            <p className={`text-sm mt-2 ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              Carregando mídias...
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Body */}
              <div>
                <h3 className={`text-lg font-semibold mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Corpo da Mensagem *
                </h3>

                {/* Textarea */}
                <textarea
                  ref={bodyTextareaRef}
                  value={bodyText}
                  onChange={(e) => handleBodyTextChange(e.target.value)}
                  placeholder="Digite o texto da mensagem aqui. Use {{1}}, {{2}} para variáveis..."
                  rows={6}
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none resize-none ${
                    hasVariableAtEdges
                      ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                      : theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500'
                  } ${
                    theme === 'dark' && !hasVariableAtEdges
                      ? 'bg-gray-700 text-white placeholder-gray-400'
                      : !hasVariableAtEdges
                      ? 'bg-white text-gray-900 placeholder-gray-500'
                      : theme === 'dark'
                      ? 'bg-gray-700 text-white placeholder-gray-400'
                      : 'bg-white text-gray-900 placeholder-gray-500'
                  }`}
                  required
                />
                <div className="flex items-center justify-between mt-1">
                  <p className={`text-xs ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    {bodyText.length}/1024 caracteres
                  </p>
                  {hasVariableAtEdges && (
                    <p className="text-xs text-red-500">
                      As variáveis não podem estar no início ou no fim do modelo.
                    </p>
                  )}
                </div>

                {/* Controles de Variável */}
                <div className={`mt-3 p-4 rounded-lg border ${
                  theme === 'dark'
                    ? 'bg-gray-700/50 border-gray-600'
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Tipo de variável {' '}
                    <span className={`text-xs font-normal ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      ℹ️
                    </span>
                  </label>
                  
                  <div className="flex gap-3 items-start">
                    {/* Dropdown de tipo de variável */}
                    <div className="flex-1">
                      <select
                        value={variableType}
                        onChange={(e) => setVariableType(e.target.value as 'number' | 'name')}
                        className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        <option value="number">Número</option>
                        <option value="name">Nome</option>
                      </select>
                    </div>

                    {/* Botão adicionar variável */}
                    <button
                      type="button"
                      onClick={insertVariableInBody}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
                        theme === 'dark'
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      <span>+</span>
                      Adicionar variável
                    </button>
                  </div>

                  {/* Descrição do tipo selecionado */}
                  <p className={`text-xs mt-2 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {variableType === 'number' ? (
                      <>
                        💡 <strong>Número:</strong> Insere automaticamente {'{{1}}'}, {'{{2}}'}, {'{{3}}'}, etc.
                      </>
                    ) : (
                      <>
                        💡 <strong>Nome:</strong> Insere {'{{}}'} para você nomear a variável
                      </>
                    )}
                  </p>
                </div>

                {/* Amostras de Variáveis */}
                {(() => {
                  const variables = detectBodyVariables(bodyText);
                  if (variables.length === 0) return null;

                  return (
                    <div className={`mt-4 p-4 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-700/50 border-gray-600'
                        : 'bg-blue-50 border-blue-200'
                    }`}>
                      <h4 className={`text-sm font-semibold mb-2 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        Amostras de variáveis *
                      </h4>
                      <p className={`text-xs mb-3 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        Inclua amostras de todas as variáveis na sua mensagem para ajudar a Meta a analisar seu modelo.
                        Para fins de proteção de privacidade, lembre-se de não incluir informações do cliente.
                      </p>

                      <div className="space-y-3">
                        {variables.map((variable, index) => (
                          <div key={variable}>
                            <label className={`block text-xs font-medium mb-1 ${
                              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              {variable}
                            </label>
                            <input
                              type="text"
                              value={bodyVariableExamples[variable] || ''}
                              onChange={(e) => {
                                setBodyVariableExamples(prev => ({
                                  ...prev,
                                  [variable]: e.target.value
                                }));
                              }}
                              placeholder={`Insira conteúdo para ${variable}`}
                              className={`w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                theme === 'dark'
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                              }`}
                              required
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Footer */}
              {/* RODAPÉ TEMPORARIAMENTE OCULTO - v1.2.6 */}
              {/* <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Rodapé (Opcional)
                  </h3>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasFooter}
                      onChange={(e) => setHasFooter(e.target.checked)}
                      className="mr-2"
                    />
                    <span className={`text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Adicionar rodapé
                    </span>
                  </label>
                </div>

                {hasFooter && (
                  <div>
                    <input
                      type="text"
                      value={footerText}
                      onChange={(e) => setFooterText(e.target.value)}
                      placeholder="Texto do rodapé"
                      maxLength={60}
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                    <p className={`text-xs mt-1 ${
                      theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      {footerText.length}/60 caracteres
                    </p>
                  </div>
                )}
              </div> */}

              {/* Buttons */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Botões (Opcional)
                  </h3>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasButtons}
                      onChange={(e) => setHasButtons(e.target.checked)}
                      className="mr-2"
                    />
                    <span className={`text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Adicionar botões
                    </span>
                  </label>
                </div>

                {hasButtons && (
                  <div className="space-y-4">
                    {buttons.map((button, index) => (
                      <div key={index} className={`p-4 rounded-lg border ${
                        theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-start justify-between mb-3">
                          <span className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Botão {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeButton(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>

                        <div className="space-y-3">
                          <select
                            value={button.type}
                            onChange={(e) => updateButton(index, 'type', e.target.value)}
                            className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              theme === 'dark'
                                ? 'bg-gray-600 border-gray-500 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          >
                            <option value="QUICK_REPLY">Resposta Rápida</option>
                            <option value="URL">Link (URL)</option>
                            <option value="PHONE_NUMBER">Telefone</option>
                          </select>

                          <div>
                            <input
                              type="text"
                              value={button.text}
                              onChange={(e) => updateButton(index, 'text', e.target.value)}
                              placeholder="Texto do botão"
                              maxLength={20}
                              className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                theme === 'dark'
                                  ? 'bg-gray-600 border-gray-500 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            />
                            <p className={`text-xs mt-1 ${
                              button.text.length >= 20
                                ? 'text-red-500'
                                : theme === 'dark'
                                ? 'text-gray-400'
                                : 'text-gray-500'
                            }`}>
                              {button.text.length}/20 caracteres
                            </p>
                          </div>

                          {button.type === 'URL' && (
                            <input
                              type="url"
                              value={button.url || ''}
                              onChange={(e) => updateButton(index, 'url', e.target.value)}
                              placeholder="https://exemplo.com"
                              className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                theme === 'dark'
                                  ? 'bg-gray-600 border-gray-500 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            />
                          )}

                          {button.type === 'PHONE_NUMBER' && (
                            <input
                              type="tel"
                              value={button.phone_number || ''}
                              onChange={(e) => updateButton(index, 'phone_number', e.target.value)}
                              placeholder="+5511999999999"
                              className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                theme === 'dark'
                                  ? 'bg-gray-600 border-gray-500 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            />
                          )}
                        </div>
                      </div>
                    ))}

                    {buttons.length < 10 && (
                      <button
                        type="button"
                        onClick={addButton}
                        className={`w-full py-2 rounded-lg border-2 border-dashed transition-colors ${
                          theme === 'dark'
                            ? 'border-gray-600 hover:border-blue-500 text-gray-400 hover:text-blue-400'
                            : 'border-gray-300 hover:border-blue-500 text-gray-600 hover:text-blue-600'
                        }`}
                      >
                        + Adicionar Botão
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                  className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className={`flex-1 py-3 rounded-lg font-medium text-white transition-colors ${
                    creating
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {creating ? 'Criando...' : 'Criar Template'}
                </button>
              </div>
            </form>

            {/* Preview - Sticky no topo */}
            <div className="sticky top-0 self-start">
              <h3 className={`text-lg font-semibold mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Pré-visualização
              </h3>
              <TemplatePreview
                header={hasHeader ? {
                  type: headerType,
                  text: headerType === 'TEXT' ? headerText : undefined,
                  mediaUrl: mediaPreviewUrl || undefined
                } : undefined}
                body={getPreviewText(bodyText)}
                footer={hasFooter ? footerText : undefined}
                buttons={hasButtons ? buttons : undefined}
              />
            </div>
          </div>
        </div>
      </div>

      {duplicateNameAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[10000] p-4">
          <div
            className={`rounded-lg shadow-xl max-w-md w-full ${
              theme === 'dark' ? 'bg-[#252527]' : 'bg-white'
            }`}
          >
            <div className="p-6">
              <h3 className={`text-xl font-semibold mb-3 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Nome de template ja em uso
              </h3>
              <p className={`mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Ja existe um template chamado "{duplicateNameAlert.name}" nesta caixa da API Oficial. Escolha outro nome antes de enviar para analise.
              </p>
              <div className={`rounded-lg p-3 mb-5 text-sm ${
                theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
              }`}>
                <div>Status: {duplicateNameAlert.status}</div>
                <div>Idioma: {duplicateNameAlert.language}</div>
                <div>Categoria: {duplicateNameAlert.category}</div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setDuplicateNameAlert(null)}
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors bg-blue-600 hover:bg-blue-700"
                >
                  Entendi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
